import { NumberRange } from '@blueprintjs/core';
import { PriceRange, SearchStatistics } from '@retro-ui/api-contract';
import { SearchResultStatus } from '@retro-ui/shared';
import cx from 'classnames';
import { cloneDeep, isEqual } from 'lodash';

import { FilterInfoCard } from './components/FilterInfoCard';
import { ReactionCountFilterCard } from './components/ReactionCountFilterCard';
import { ScoreFilterCard } from './components/ScoreFilterCard';
import { SearchFilterCard } from './components/SearchFilterCard';
import { SortingCard, SortingElement } from './components/SortingCard';
import { StatusFilterCard } from './components/StatusFilterCard';
import cls from './SearchResultFilters.module.css';
import { PriceFilterCard } from './components/PriceFilterCard';

export interface ResultFilters {
  pageSize: number;
  sorting: SortingElement[];
  targetFilter: string;
  status: SearchResultStatus[];
  result?: NumberRange;
  reactionCount?: NumberRange;
  price?: PriceRange;
}

const PAGE_SIZE = 20;

const INITIAL_FILTERS: ResultFilters = {
  pageSize: PAGE_SIZE,
  sorting: [],
  targetFilter: '',
  status: [SearchResultStatus.COMPLETED, SearchResultStatus.FAILED, SearchResultStatus.RUNNING],
};
export function getInitialFilters(): ResultFilters {
  return cloneDeep(INITIAL_FILTERS);
}

interface SearchResultFiltersProps {
  className: string;
  resultCount: number;
  statistics?: SearchStatistics;
  filters: ResultFilters;
  onFiltersChange: (filters: ResultFilters) => void;
  hasReferenceCount: boolean;
}
export function SearchResultFilters(props: SearchResultFiltersProps) {
  const { resultCount, statistics, filters, onFiltersChange, hasReferenceCount } = props;

  const onResetFilters = async () => {
    onFiltersChange(getInitialFilters());
  };

  const onFilterChange = (name: keyof ResultFilters) => (value: unknown) => {
    if (isEqual(filters[name], value)) {
      return;
    }
    onFiltersChange({
      ...filters,
      [name]: value,
    });
  };

  return (
    <div className={cx(cls.filters, props.className)}>
      <FilterInfoCard
        completedTotal={resultCount}
        onReset={onResetFilters}
        areFiltersInitial={isEqual(filters, INITIAL_FILTERS)}
      />

      <SortingCard
        attributes={[
          { field: 'result', label: 'Score' },
          { field: 'price', label: 'SM Price' },
          { field: 'reactionCount', label: 'Reactions' },
          ...(hasReferenceCount ? [{ field: 'referenceCount', label: 'References' }] : []),
          // { field: 'targetMetadata.name', label: 'Name' },
        ]}
        label="SORT BY"
        sorting={filters.sorting}
        onChange={onFilterChange('sorting')}
      />

      <SearchFilterCard
        label="TARGET SMILES / NAME"
        value={filters.targetFilter}
        onChange={onFilterChange('targetFilter')}
        data-cy="search-target-filter-card"
      />

      <StatusFilterCard value={filters.status} onChange={onFilterChange('status')} />

      <ScoreFilterCard value={filters.result} onChange={onFilterChange('result')} />

      {statistics?.reactionCount && (
        <ReactionCountFilterCard
          reactionCountStatistics={statistics.reactionCount}
          value={filters.reactionCount}
          onChange={onFilterChange('reactionCount')}
        />
      )}

      {statistics?.price && (
        <PriceFilterCard
          priceStatistics={statistics.price}
          value={filters.price}
          onChange={onFilterChange('price')}
        />
      )}
    </div>
  );
}
